import { useState, useEffect } from 'react';
import { useCookieConsent } from '@/contexts/CookieContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { getLocalizedRoute } from '@/lib/routes';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';

/**
 * Cookie Preferences Modal
 * 
 * Allows users to manage their cookie preferences with granular control.
 * Strictly necessary cookies cannot be disabled (always enabled).
 * Changes are not saved until user clicks "Save Preferences" or "Accept All"/"Reject All".
 */
export const CookiePreferences: React.FC = () => {
  const { language } = useLanguage();
  const {
    consent,
    isPreferencesOpen,
    closePreferences,
    updatePreferences,
    acceptAll,
    rejectAll,
    clearConsent,
  } = useCookieConsent();

  // Local state for temporary preferences (not saved until user clicks save)
  const [tempPreferences, setTempPreferences] = useState<{
    strictlyNecessary: boolean;
    analytics: boolean;
  }>({
    strictlyNecessary: true,
    analytics: true, // Default to true so checkbox is checked by default
  });

  // Update temp preferences when modal opens or consent changes
  useEffect(() => {
    if (isPreferencesOpen) {
      // When modal opens, initialize temp preferences from consent or defaults
      setTempPreferences(
        consent?.preferences || {
          strictlyNecessary: true,
          analytics: true, // Default to true so checkbox is checked by default
        }
      );
    }
  }, [isPreferencesOpen, consent]);

  const handleSave = () => {
    // Save the temporary preferences
    updatePreferences({
      analytics: tempPreferences.analytics,
    });
    // Close the modal after saving
    closePreferences();
  };

  const handleAnalyticsChange = (checked: boolean) => {
    // Only update local state, don't save yet
    setTempPreferences((prev) => ({
      ...prev,
      analytics: checked,
    }));
  };

  const handleAcceptAll = () => {
    // Set temp preferences to accept all, then save
    setTempPreferences({
      strictlyNecessary: true,
      analytics: true,
    });
    // Accept all will save and close the modal
    acceptAll();
  };

  const handleRejectAll = () => {
    // Set temp preferences to reject all, then save
    setTempPreferences({
      strictlyNecessary: true,
      analytics: false,
    });
    // Reject all will save and close the modal
    rejectAll();
  };

  // Handle dialog close - only close if user explicitly wants to (not on outside click)
  const handleDialogChange = (open: boolean) => {
    if (!open) {
      // If closing, reset temp preferences to current consent to discard unsaved changes
      setTempPreferences(
        consent?.preferences || {
          strictlyNecessary: true,
          analytics: true,
        }
      );
      closePreferences();
    }
  };

  return (
    <Dialog open={isPreferencesOpen} onOpenChange={handleDialogChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Cookie Preferences</DialogTitle>
          <DialogDescription>
            Manage your cookie preferences. You can enable or disable different 
            types of cookies below. Strictly necessary cookies are always enabled 
            as they are required for the website to function properly.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Strictly Necessary Cookies */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5 flex-1">
                <Label htmlFor="strictly-necessary" className="text-base font-semibold">
                  Strictly Necessary Cookies
                </Label>
                <p className="text-sm text-muted-foreground">
                  These cookies are essential for the website to function properly. 
                  They enable core functionality such as security, network management, 
                  and accessibility. You cannot disable these cookies.
                </p>
              </div>
              <Switch
                id="strictly-necessary"
                checked={true}
                disabled
                aria-label="Strictly necessary cookies (always enabled)"
                className="ml-4"
              />
            </div>
            <div className="text-xs text-muted-foreground pl-1">
              <p className="font-medium mb-1">Examples:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Session management</li>
                <li>Security features</li>
                <li>Load balancing</li>
                <li>Cookie consent preferences</li>
              </ul>
            </div>
          </div>

          <Separator />

          {/* Analytics Cookies */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5 flex-1">
                <Label htmlFor="analytics" className="text-base font-semibold">
                  Analytics Cookies
                </Label>
                <p className="text-sm text-muted-foreground">
                  These cookies help us understand how visitors interact with our website 
                  by collecting and reporting information anonymously. This helps us improve 
                  our website and user experience.
                </p>
              </div>
              <Switch
                id="analytics"
                checked={tempPreferences.analytics}
                onCheckedChange={handleAnalyticsChange}
                aria-label="Analytics cookies"
                className="ml-4"
              />
            </div>
            <div className="text-xs text-muted-foreground pl-1">
              <p className="font-medium mb-1">Examples:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Google Analytics 4 (GA4) - when enabled</li>
                <li>Page view tracking</li>
                <li>User behavior analysis</li>
                <li>Performance metrics</li>
                <li>Google secure cookies (__Secure-3PAPISID, __Secure-3PSID, etc.)</li>
              </ul>
              {!tempPreferences.analytics && (
                <p className="mt-2 italic">
                  Note: Analytics cookies are currently disabled. No tracking scripts 
                  will be loaded until you enable this option.
                </p>
              )}
              {tempPreferences.analytics && (
                <details className="mt-3">
                  <summary className="cursor-pointer font-medium hover:text-foreground">
                    View Google Cookies Details
                  </summary>
                  <div className="mt-2 pl-2 space-y-2 border-l-2 border-muted">
                    <p className="text-xs">
                      When analytics is enabled, Google may set the following secure cookies:
                    </p>
                    <ul className="text-xs space-y-1 list-disc list-inside">
                      <li><code className="bg-muted px-1 rounded">__Secure-3PAPISID</code> - Google API authentication</li>
                      <li><code className="bg-muted px-1 rounded">__Secure-3PSID</code> - Google session ID</li>
                      <li><code className="bg-muted px-1 rounded">__Secure-3PSIDCC</code> - Google session with consent</li>
                      <li><code className="bg-muted px-1 rounded">__Secure-1PAPISID</code> - Google API (first-party)</li>
                      <li><code className="bg-muted px-1 rounded">__Secure-1PSID</code> - Google session (first-party)</li>
                      <li><code className="bg-muted px-1 rounded">_ga</code>, <code className="bg-muted px-1 rounded">_gid</code> - Google Analytics</li>
                    </ul>
                    <p className="text-xs italic mt-2">
                      These cookies are set by Google domains and are subject to your browser's 
                      third-party cookie settings. They help provide personalized experiences 
                      and measure website performance.
                    </p>
                  </div>
                </details>
              )}
            </div>
          </div>

          <Separator />

          {/* Additional Information */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <h3 className="text-sm font-semibold">About Your Privacy</h3>
            <p className="text-xs text-muted-foreground">
              We respect your privacy and comply with GDPR and EU ePrivacy Directive 
              requirements. Your consent preferences are stored locally in your browser 
              and can be changed at any time. For more information, please read our{' '}
              <a
                href={getLocalizedRoute('/privacy-policy', language)}
                className="text-primary underline hover:text-primary/80 focus:outline-none focus:ring-2 focus:ring-ring rounded"
                target="_blank"
                rel="noopener noreferrer"
              >
                Cookie Policy
              </a>
              {' '}and{' '}
              <a
                href={getLocalizedRoute('/privacy-policy', language)}
                className="text-primary underline hover:text-primary/80 focus:outline-none focus:ring-2 focus:ring-ring rounded"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
              .
            </p>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="ghost"
            onClick={() => {
              clearConsent();
              closePreferences();
            }}
            className="w-full sm:w-auto text-muted-foreground hover:text-destructive"
            aria-label="Clear all cookie preferences"
          >
            Clear All Data
          </Button>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={handleRejectAll}
              className="flex-1 sm:flex-none"
              aria-label="Reject all non-essential cookies"
            >
              Reject All
            </Button>
            <Button
              variant="default"
              onClick={handleAcceptAll}
              className="flex-1 sm:flex-none"
              aria-label="Accept all cookies"
            >
              Accept All
            </Button>
            <Button
              onClick={handleSave}
              className="flex-1 sm:flex-none"
              aria-label="Save preferences"
            >
              Save Preferences
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

